"use client"

import { useState } from "react"
import { FolderPlus, ChevronRight } from "lucide-react"
import { Button } from "../../../components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import type { Folder } from "../../../lib/types"
import { saveFolder } from "../../../API"

interface BreadcrumbItem {
    id: string | null
    name: string
}

interface ToolbarProps {
    breadcrumbs: BreadcrumbItem[]
    onCreateFolder: (name: string, id: number, projectIds: number[]) => void
    onNavigate: (folderId: string | null) => void
}

export function Toolbar({
    breadcrumbs,
    onCreateFolder,
    onNavigate,
}: ToolbarProps) {
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [folderName, setFolderName] = useState("")

    const handleCreate = async () => {
        if (folderName.trim()) {

            const data = await saveFolder(folderName.trim(), currentCrumb.id)

            onCreateFolder(folderName.trim(), data.id, [])
            setFolderName("")
            setIsDialogOpen(false)
        }
    }

    const currentCrumb = breadcrumbs[breadcrumbs.length - 1]
    const isRoot = breadcrumbs.length === 1

    return (
        <>
            <header className="flex items-center justify-between border-b border-border px-6 py-4">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center gap-1 text-sm" aria-label="Navegacao">
                        {breadcrumbs.map((crumb, i) => {
                            const isLast = i === breadcrumbs.length - 1
                            return (
                                <span key={crumb.id ?? "root"} className="flex items-center gap-1">
                                    {i > 0 && (
                                        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />
                                    )}
                                    {isLast ? (
                                        <span className="truncate font-semibold text-foreground">
                                            {crumb.name}
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => onNavigate(crumb.id)}
                                            className="truncate text-muted-foreground transition-colors hover:text-foreground"
                                        >
                                            {crumb.name}
                                        </button>
                                    )}
                                </span>
                            )
                        })}
                    </nav>
                    <p className="text-xs text-muted-foreground">
                        {isRoot
                            ? "Organize seus projetos em pastas"
                            : "Subpastas e projetos nesta pasta"}
                    </p>
                </div>
                <Button
                    onClick={() => setIsDialogOpen(true)}
                    className="ml-4 shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                    <FolderPlus className="mr-2 size-4" />
                    {isRoot ? "Nova Pasta" : "Nova Subpasta"}
                </Button>
            </header>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {isRoot ? "Criar Nova Pasta" : `Criar Subpasta em "${currentCrumb.name}"`}
                        </DialogTitle>
                        <DialogDescription>
                            Escolha um nome para a nova {isRoot ? "pasta" : "subpasta"}.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        placeholder={isRoot ? "Nome da pasta" : "Nome da subpasta"}
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                        autoFocus
                    />
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setFolderName("")
                                setIsDialogOpen(false)
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={!folderName.trim()}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                            Criar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}